import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { CountBadgePipe } from '../../../core/pipes/badge.pipe';
import { AuthService } from '../../auth/auth.service';
import { MOLLAK_SERVICE_REQUEST_STATUS_CLASSES, MollakServiceRequestStatus } from '../../happiness-center/mollak-service-requests/mollak-service-requests.type';
import { FilterConfig, GlobalFilterPanelComponent } from '../components/global-filter-panel/global-filter-panel.component';
import { DashboardService } from '../dashboard.service';
import { DashboardEngineService } from '../services/dashboard-engine.service';
import { ActivityTimelineComponent } from '../widgets/shared/activity-timeline/activity-timeline.component';
import { KpiCard, KpiCardGridComponent } from '../widgets/shared/kpi-card-grid/kpi-card-grid.component';
import { LabeledCountItem, LabeledCountListComponent } from '../widgets/shared/labeled-count-list/labeled-count-list.component';
import { SurveyOverviewComponent } from '../widgets/shared/survey-overview/survey-overview.component';

const TYPE_BADGE_CLASS: Record<string, string> = {
  inquiry: 'text-blue-700 bg-blue-50',
  complaint: 'text-red-700 bg-red-50',
  suggestion: 'text-green-700 bg-green-50',
};

@Component({
  selector: 'app-support-dashboard',
  standalone: true,
  imports: [
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    NgClass,
    DatePipe,
    RouterLink,
    CountBadgePipe,
    GlobalFilterPanelComponent,
    KpiCardGridComponent,
    LabeledCountListComponent,
    ActivityTimelineComponent,
    SurveyOverviewComponent,
  ],
  templateUrl: './support-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: block; width: 100%;' },
})
export class SupportDashboardComponent {
  private engine = inject(DashboardEngineService);
  protected dashboardSvc = inject(DashboardService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private auth = inject(AuthService);
  private matDialog = inject(MatDialog);

  readonly dashboardType = 'support';
  filters: any = {};

  get hasTicketPermission(): boolean {
    return this.auth.canAccess(['happiness_center_view']);
  }

  get hasMollakPermission(): boolean {
    return this.auth.canAccess(['mollak_service_request_view']);
  }

  ticketData: any = null;
  slaData: any = null;
  categoryData: any = null;
  openData: any = null;
  mollakData: any = null;
  mollakOpenData: any = null;

  loadingTicket = false;
  loadingSla = false;
  loadingCategory = false;
  loadingOpen = false;
  loadingMollak = false;
  loadingMollakOpen = false;

  ticketKpis: KpiCard[] = [];
  typeItems: LabeledCountItem[] = [];
  communityItems: LabeledCountItem[] = [];
  mollakKpis: KpiCard[] = [];
  mollakTypeItems: LabeledCountItem[] = [];
  mollakCommunityItems: LabeledCountItem[] = [];

  readonly filterConfig: FilterConfig = {
    community: true,
    period: true,
    dateRange: false,
  };

  onFiltersChanged(f: any): void {
    this.filters = { ...f };
    this.dashboardSvc.activeFilters.set(this.filters);
    this.loadAll();
    this.cdr.markForCheck();
  }

  private loadAll(): void {
    this.loadTicketSummary();
    this.loadSlaTracking();
    this.loadCategoryBreakdown();
    this.loadOpenTickets();
    this.loadMollakSummary();
    this.loadMollakOpenRequests();
  }

  private loadTicketSummary(): void {
    this.loadingTicket = true;
    this.engine
      .getWidgetData(this.dashboardType, 'ticketSummary', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const d = res.record?.data ?? {};
          this.ticketData = d;
          this.ticketKpis = [
            {
              label: 'Total Tickets',
              value: d.total ?? 0,
              icon: 'confirmation_number',
              color: 'text-blue-600',
              bgColor: 'bg-blue-50',
              subtitle: 'All Happiness Center tickets (inquiries, complaints, suggestions) created within the selected date range.',
            },
            {
              label: 'Open',
              value: d.open ?? 0,
              icon: 'inbox',
              color: 'text-indigo-600',
              bgColor: 'bg-indigo-50',
              subtitle: 'Tickets with status Open or Under Process — created within the selected date range and not yet resolved.',
            },
            {
              label: 'Pending',
              value: d.pending ?? 0,
              icon: 'pending_actions',
              color: 'text-yellow-600',
              bgColor: 'bg-yellow-50',
              subtitle: 'Tickets currently marked Under Process — being actively worked on by staff.',
            },
            {
              label: 'Closed',
              value: d.closed ?? 0,
              icon: 'task_alt',
              color: 'text-green-600',
              bgColor: 'bg-green-50',
              subtitle: 'Tickets with status Resolved within the selected date range.',
            },
            {
              label: 'Overdue',
              value: d.overdue ?? 0,
              icon: 'alarm',
              color: 'text-red-600',
              bgColor: 'bg-red-50',
              subtitle: 'Open or Under Process tickets that have been unresolved for more than 3 days since creation.',
            },
          ];
          this.loadingTicket = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingTicket = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadSlaTracking(): void {
    if (!this.hasTicketPermission) return;
    this.loadingSla = true;
    this.engine
      .getWidgetData(this.dashboardType, 'slaTracking', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.slaData = res.record?.data ?? {};
          this.loadingSla = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingSla = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadCategoryBreakdown(): void {
    if (!this.hasTicketPermission) return;
    this.loadingCategory = true;
    this.engine
      .getWidgetData(this.dashboardType, 'categoryBreakdown', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.categoryData = res.record?.data ?? {};
          this.typeItems = (this.categoryData?.by_type ?? []).map((t: any) => ({
            key: t.type ?? 'other',
            label: t.type || 'Other',
            count: t.count,
            badgeClass: this.getTypeBadgeClass(t.type),
            routerLink: '/happiness-center/online',
            queryParams: { type: t.type },
          }));
          this.communityItems = (this.categoryData?.by_community ?? []).map((c: any) => ({
            key: c.community_name ?? 'n/a',
            label: c.community_name || 'N/A',
            count: c.count,
            badgeClass: 'text-gray-700 bg-gray-100',
          }));
          this.loadingCategory = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingCategory = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadOpenTickets(): void {
    if (!this.hasTicketPermission) return;
    this.loadingOpen = true;
    this.engine
      .getWidgetData(this.dashboardType, 'openTickets', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.openData = res.record?.data ?? {};
          this.loadingOpen = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingOpen = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadMollakSummary(): void {
    if (!this.hasMollakPermission) return;
    this.loadingMollak = true;
    this.engine
      .getWidgetData(this.dashboardType, 'mollakSummary', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const d = res.record?.data ?? {};
          this.mollakData = d;
          this.mollakKpis = [
            {
              label: 'Total Requests',
              value: d.total ?? 0,
              icon: 'assignment',
              color: 'text-blue-600',
              bgColor: 'bg-blue-50',
              subtitle: 'Mollak service requests submitted within the selected date range (first email date when the submitted date is missing).',
            },
            {
              label: 'New',
              value: d.new ?? 0,
              icon: 'fiber_new',
              color: 'text-amber-600',
              bgColor: 'bg-amber-50',
              subtitle: 'Requests still awaiting a response.',
            },
            {
              label: 'Responded',
              value: d.responded ?? 0,
              icon: 'reply',
              color: 'text-indigo-600',
              bgColor: 'bg-indigo-50',
              subtitle: 'Requests responded to but not yet completed.',
            },
            {
              label: 'Completed',
              value: d.completed ?? 0,
              icon: 'task_alt',
              color: 'text-green-600',
              bgColor: 'bg-green-50',
              subtitle: 'Requests marked completed.',
            },
            {
              label: 'Overdue',
              value: d.overdue ?? 0,
              icon: 'alarm',
              color: 'text-red-600',
              bgColor: 'bg-red-50',
              subtitle: 'New requests whose 5-day KPI due date has passed.',
            },
          ];
          this.mollakTypeItems = (d.by_type ?? []).map((t: any) => ({
            key: t.type ?? 'other',
            label: t.type || 'Other',
            count: t.count,
            badgeClass: 'text-purple-700 bg-purple-50',
          }));
          this.mollakCommunityItems = (d.by_community ?? []).map((c: any) => ({
            key: c.community_name ?? 'not-linked',
            label: c.community_name || 'Not linked',
            count: c.count,
            badgeClass: 'text-gray-700 bg-gray-100',
          }));
          this.loadingMollak = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingMollak = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadMollakOpenRequests(): void {
    if (!this.hasMollakPermission) return;
    this.loadingMollakOpen = true;
    this.engine
      .getWidgetData(this.dashboardType, 'mollakOpenRequests', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.mollakOpenData = res.record?.data ?? {};
          this.loadingMollakOpen = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingMollakOpen = false;
          this.cdr.markForCheck();
        },
      });
  }

  openMollakRequest(id: number): void {
    const onAction = new EventEmitter<void>();
    onAction.subscribe(() => {
      this.loadMollakSummary();
      this.loadMollakOpenRequests();
    });
    import('../../happiness-center/mollak-service-requests/mollak-service-requests-list/dialogs/mollak-service-request-detail-dialog/mollak-service-request-detail-dialog.component').then((m) => {
      this.matDialog.open(m.MollakServiceRequestDetailDialogComponent, {
        data: { id, onAction },
      });
    });
  }

  getTypeBadgeClass(type: string): string {
    return TYPE_BADGE_CLASS[type?.toLowerCase()] ?? 'text-gray-700 bg-gray-50';
  }

  getSlaBarColor(pct: number): string {
    if (pct >= 90) return 'bg-green-500';
    if (pct >= 70) return 'bg-yellow-500';
    return 'bg-red-500';
  }

  getStatusBadgeClass(status: string): string {
    const s = status?.toLowerCase();
    if (s === 'open') return 'bg-blue-100 text-blue-700';
    if (s === 'under process') return 'bg-amber-100 text-amber-700';
    if (s === 'resolved') return 'bg-green-100 text-green-700';
    return 'bg-gray-100 text-gray-500';
  }

  getMollakStatusBadgeClass(status: MollakServiceRequestStatus): string {
    return MOLLAK_SERVICE_REQUEST_STATUS_CLASSES[status] ?? 'bg-gray-100 text-gray-500';
  }

  ticketQueryParams(t: any): any {
    return { happiness_center_id: t.id, type: t.type };
  }
}
